/* eslint-disable */
const defaultFn = v => v;
const propValidator = () => true;
const defaultModel = {
  prop: 'value',
  event: 'input',
};

function toCamel(str) {
  return str.replace(/-([a-z])/ig, (m, letter) => letter.toUpperCase());
}

function getPropOptionMap(props, options = {}) {
  const propOptionMap = {};

  const { model } = options;

  function parseProp(propOption) {
    const {
      name: propName,
      transform,
      ...others } = propOption;
    const camelName = propName.replace(/^[a-z]/, letter => letter.toUpperCase());
    const defaultName = `default${camelName}`;
    const dataName = `data${camelName}`;

    let events = [];
    if (propOption.event) {
      events = events.concat(propOption.event);
    } else if (model.prop === propName) {
      events = events.concat(model.event);
    } else {
      events = events.concat(`update:${propName}`);
    }

    return {
      events,
      defaultName,
      dataName,
      ...others,
    };
  }

  props.forEach((prop) => {
    const defaultOption = {
      transform: defaultFn,
      reverse: defaultFn,
      alias: [],
    };
    let propOption;
    if (typeof prop === 'string') {
      propOption = Object.assign({}, defaultOption, { name: prop });
    } else {
      propOption = Object.assign({}, defaultOption, prop);
    }

    propOptionMap[propOption.name] = parseProp(propOption);
  });

  return propOptionMap;
}

export default function(props, options = {}) {
  return function(component) {
    const model = options.model || defaultModel;
    const propOptionMap = getPropOptionMap(props, { model });

    const defineProps = {};
    const defineWatches = {};
    let defineEvents = [];
    const defineMethods = {};

    const propsIsArray = Array.isArray(component.props);
    const propsKeys = propsIsArray ? component.props : Object.keys(component.props);
    const camelPropsKeys = propsKeys.map(key => toCamel(key));

    for (const propName in propOptionMap) {
      const { events, alias, defaultName, dataName } = propOptionMap[propName];

      defineProps[propName] = propsIsArray ? { validator: propValidator } : component.props[propName];
      defineProps[defaultName] = propsIsArray ? { validator: propValidator } : component.props[defaultName];
      if (alias) {
        alias.forEach((prop) => {
          defineProps[prop] = defineProps[propName];
        });
      }
      defineEvents = defineEvents.concat(events);

      // watch default prop
      defineWatches[defaultName] = {
        handler(v) {
          if (
            defaultName in this.$vnode.componentOptions.propsData
            && !(propName in this.$vnode.componentOptions.propsData)
          ) {
            this.$data[dataName] = v;
          }
        },
        immediate: true,
      };

      // watch alias
      alias.forEach((aliasItem) => {
        defineWatches[aliasItem] = {
          handler(v) {
            if (
              aliasItem in this.$vnode.componentOptions.propsData
              && !(propName in this.$vnode.componentOptions.propsData)
            ) {
              this.$data[dataName] = v;
            }
          },
          immediate: true,
        };
      });

      // watch prop
      defineWatches[propName] = {
        handler(v) {
          if (propName in this.$vnode.componentOptions.propsData) {
            this.$data[dataName] = v;
          }
        },
        immediate: true,
      };
    }

    if (component.methods) {
      for (const key in component.methods) {
        defineMethods[key] = function(...args) {
          if (this.$refs.component) {
            return this.$refs.component[key](...args);
          }
        };
      }
    }

    const { name } = component;
    component.name = `${name}-controlled`;
    return {
      name,
      inheritAttrs: false,
      model: {
        prop: model.prop,
        event: Array.isArray(model.event) ? model.event[0] : model.event,
      },
      data() {
        const data = {};
        for (const propName in propOptionMap) {
          const { dataName } = propOptionMap[propName];

          data[dataName] = undefined;
        }
        return { ...data };
      },
      props: {
        ...defineProps,
      },
      computed: {
        _listeners() {
          const others = {};
          for (const event in this.$listeners) {
            if (defineEvents.indexOf(event) === -1) {
              others[event] = this.$listeners[event];
            }
          }
          return others;
        },
      },
      watch: defineWatches,
      render(h) {
        const propMap = {};
        const handlerMap = {};
        for (const propName in propOptionMap) {
          const { dataName, events } = propOptionMap[propName];
          if (
            propName in this.$vnode.componentOptions.propsData
            || typeof this[dataName] !== 'undefined'
          ) {
            propMap[propName] = this[dataName];
          }
          // 只监听第一个定义的事件，参数取第一个事件参数
          handlerMap[events[0]] = (...args) => this.updateData(propName, ...args);
        }

        const attrs = {};
        for (const attrKey in this.$attrs) {
          const camelAttrKey = toCamel(attrKey);
          if (camelPropsKeys.indexOf(camelAttrKey) === -1) {
            attrs[attrKey] = this.$attrs[attrKey];
          }
        }

        return h(component, {
          props: {
            ...this.$attrs,
            ...propMap,
          },

          attrs: {
            ...attrs,
          },

          on: {
            ...this._listeners,
            ...handlerMap,
          },

          scopedSlots: {
            ...this.$scopedSlots,
          },

          ref: 'component',
        });
      },
      methods: {
        ...defineMethods,
        updateData(propName, v, ...args) {
          propOptionMap[propName].events.forEach((event) => {
            this.$emit(event, v, ...args);
          });
          if (!(propName in this.$vnode.componentOptions.propsData)) {
            this[propOptionMap[propName].dataName] = v;
            return true;
          }
        },
      },
    };
  };
};
